import axios from "axios";
import React, { useEffect, useState } from "react";
import { fetchCheckoutDisclaimer, CheckoutDisclaimer } from "../../../utils/checkoutDisclaimer";
import { Alert, Nav, NavItem, NavLink, TabContent, TabPane, Modal, ModalHeader, ModalBody, Button, Row, Col, Badge, Label, UncontrolledTooltip } from "reactstrap";
import Breadcrumbs from "../../../Components/Common/Breadcrumb";
import classnames from 'classnames';
import { ShoppingCart, Plus, Minus, Trash2, ExternalLink, Building2, MapPin, Globe, Linkedin, Facebook, Twitter, Instagram, Youtube, Phone, Check } from 'lucide-react';
import PartnerCommunicationPreferences from "../../../Components/Common/PartnerCommunicationPreferences";
import ReactMarkdown from 'react-markdown';

type PlanOptionals = {
    id: string;
    group: string;
    title: string;
    description: string;
    price: string;
    validity?: string;
    features: string[];
    selected: boolean;
    pricing_type?: 'fixed' | 'contact' | 'external';
}

type AddOn = {
    id: string;
    title: string;
    description: string;
    price: string;
    validity?: string;
    features: string[];
    selected: boolean;
    pricing_type?: 'fixed' | 'contact' | 'external';
}

type SubscriptionAddOn = {
    id: string;
    title: string;
    description: string;
    price: string;
    period: string;
    features: string[];
    selected: boolean;
    pricing_type?: 'fixed' | 'contact' | 'external';
}

type Plan = {
    id: string;
    title: string;
    description: string;
    price: string;
    validity?: string;
    features: string[];
    addons: AddOn[];
    subscriptionAddOns?: SubscriptionAddOn[];
    optionals: PlanOptionals[];
    pricing_type?: 'fixed' | 'contact' | 'external';
    external_url?: string;
    product_group_id?: number | null;
}

type ActivePlan = {
    planId: string;
    planTitle: string;
    productGroupId: number;
    purchasedAt: string | null;
    expiresAt: string | null;
    status: 'active' | 'cancelling';
} | null;

type OneTimeProduct = {
    id: string;
    title: string;
    description: string;
    price: string;
    quantifiable: boolean;
    features: string[];
    pricing_type?: 'fixed' | 'contact' | 'external';
    external_url?: string;
}

type CartItem = {
    product: OneTimeProduct;
    quantity: number;
}

const getPlanTotal = (plan: Plan) => {
    const planPricingType = plan.pricing_type || 'fixed';

    // If plan pricing_type is 'contact', return null to indicate "Contact for pricing"
    if (planPricingType === 'contact') {
        return null;
    }

    // For external pricing, price is optional - allow calculation even if plan price is null
    // For fixed pricing, if price is null, return null
    if (planPricingType !== 'external' && (plan.price === null || plan.price === undefined)) {
        return null;
    }

    // Check if any selected add-on, optional, or subscription add-on has contact pricing or null price (and not external)
    const hasContactAddon = plan.addons.some(a => {
        if (!a.selected) return false;
        const addonPricingType = a.pricing_type || 'fixed';
        return addonPricingType === 'contact' || (addonPricingType !== 'external' && (a.price === null || a.price === undefined));
    });
    const hasContactOptional = plan.optionals.some(o => {
        if (!o.selected) return false;
        const optionalPricingType = o.pricing_type || 'fixed';
        return optionalPricingType === 'contact' || (optionalPricingType !== 'external' && (o.price === null || o.price === undefined));
    });
    const hasContactSubAddon = (plan.subscriptionAddOns || []).some(a => {
        if (!a.selected) return false;
        const subAddonPricingType = a.pricing_type || 'fixed';
        return subAddonPricingType === 'contact' || (subAddonPricingType !== 'external' && (a.price === null || a.price === undefined));
    });

    if (hasContactAddon || hasContactOptional || hasContactSubAddon) {
        return null;
    }

    // Calculate total - for external pricing, plan price might be null, so use 0
    let total = (plan.price !== null && plan.price !== undefined) ? parseFloat(plan.price) || 0 : 0;
    plan.addons.forEach(a => { if (a.selected && a.price !== null && a.price !== undefined) total += parseFloat(a.price) || 0; });
    plan.optionals.forEach(o => { if (o.selected && o.price !== null && o.price !== undefined) total += parseFloat(o.price) || 0; });
    (plan.subscriptionAddOns || []).forEach(a => { if (a.selected && a.price !== null && a.price !== undefined) total += parseFloat(a.price) || 0; });
    return total.toFixed(2);
};

const getPlanRecurring = (plan: Plan) => {
    // Sum all selected subscription add-ons by period (skip if pricing_type is 'contact' or price is null for non-external)
    let recurring: { [period: string]: number } = {};

    // Include plan's price if plan is subscription-based and not contact pricing
    const planPricingType = plan.pricing_type || 'fixed';
    if (plan.validity && plan.validity !== '' && plan.validity !== 'lifetime' && plan.validity !== 'custom' &&
        planPricingType !== 'contact' && plan.price !== null && plan.price !== undefined) {
        const period = plan.validity;
        recurring[period] = (recurring[period] || 0) + (parseFloat(plan.price) || 0);
    }

    // Sum all selected subscription add-ons by period (skip if pricing_type is 'contact' or price is null for non-external)
    (plan.subscriptionAddOns || []).forEach(a => {
        if (a.selected) {
            const addonPricingType = a.pricing_type || 'fixed';
            if (addonPricingType !== 'contact' && a.price !== null && a.price !== undefined) {
                recurring[a.period] = (recurring[a.period] || 0) + (parseFloat(a.price) || 0);
            }
        }
    });

    // Note: Addons are NOT included here because they are one-time purchases with validity periods,
    // not subscription-based recurring charges

    // Also include plan optionals with validity (subscription-based optionals)
    plan.optionals.forEach(o => {
        if (o.selected && o.validity && o.validity !== '' && o.validity !== 'lifetime' && o.validity !== 'custom') {
            const optionalPricingType = o.pricing_type || 'fixed';
            if (optionalPricingType !== 'contact' && o.price !== null && o.price !== undefined) {
                const period = o.validity; // Use validity as period (month, year, etc.)
                recurring[period] = (recurring[period] || 0) + (parseFloat(o.price) || 0);
            }
        }
    });
    return recurring;
};

const PackagesPricing = ({ domain }: { domain: number }) => {
    // Helper function to format price with commas using built-in toLocaleString
    const formatPrice = (price: string | number | null | undefined): string => {
        if (price == null) return '';
        const num = Number(price);
        return isNaN(num) ? String(price) : num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    };

    const [activeTab, setActiveTab] = useState<string>('1');
    const [showloader, setshowloader] = useState(true);
    const [alertMsgg, setAlertMsgg] = useState<any>({ open: false, type: "success", content: "Messages here.", time: "2500" });

    // Get user role to hide buy buttons for employees and partners
    const userRole = localStorage.getItem('userole_');
    const isEmployeeOrPartner = userRole === 'emp' || userRole === 'prt';
    const handleAlertMsggClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") { return; }
        setAlertMsgg({ ...alertMsgg, open: false });
    };

    const [plansState, setPlansState] = useState<Plan[]>([]);
    const [oneTimeProducts, setOneTimeProducts] = useState<OneTimeProduct[]>([]);
    const [cart, setCart] = useState<CartItem[]>([]);
    const [showCart, setShowCart] = useState(false);

    // Categories
    const [categories, setCategories] = useState<any[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
    const [onetimeCategories, setOnetimeCategories] = useState<any[]>([]);
    const [selectedOnetimeCategoryId, setSelectedOnetimeCategoryId] = useState<string>("");

    // Partners (optional filter: one partner)
    const [partners, setPartners] = useState<any[]>([]);
    const [selectedPartnerId, setSelectedPartnerId] = useState<string>("");

    // Communication preferences
    const [commPreferences, setCommPreferences] = useState<any>(null);
    const [loadingCommPrefs, setLoadingCommPrefs] = useState(false);

    // Partner details modal
    const [partnerDetailsModal, setPartnerDetailsModal] = useState(false);
    const [selectedPartnerDetails, setSelectedPartnerDetails] = useState<any>(null);
    const [loadingPartnerDetails, setLoadingPartnerDetails] = useState(false);

    // Product details modal
    const [productDetailsModal, setProductDetailsModal] = useState(false);
    const [selectedProductDetails, setSelectedProductDetails] = useState<{
        type: 'addon' | 'subscriptionAddon' | 'optional';
        title: string;
        price: string | null;
        description: string;
        features: string[];
        period?: string;
        validity?: string;
    } | null>(null);

    // Purchase Agreement Modal
    const [purchaseAgreementModal, setPurchaseAgreementModal] = useState(false);
    const [purchaseAgreements, setPurchaseAgreements] = useState<any[]>([]);
    const [requiredAgreementIds, setRequiredAgreementIds] = useState<number[]>([]);
    const [acceptedAgreementIds, setAcceptedAgreementIds] = useState<number[]>([]);
    const [isLoadingAgreements, setIsLoadingAgreements] = useState(false);
    const [pendingCheckoutData, setPendingCheckoutData] = useState<{
        type: 'plan' | 'cart';
        planIdx?: number;
        payload?: any;
    } | null>(null);

    // Checkout Disclaimer
    const [checkoutDisclaimer, setCheckoutDisclaimer] = useState<CheckoutDisclaimer | null>(null);
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

    // Active plan in this domain (fetched on load)
    const [activePlan, setActivePlan] = useState<ActivePlan>(null);

    // Cache for products by partner-category combination (in-memory only, cleared on page refresh)
    // Cache key: partner + category (since domain is constant for the page)
    type ProductsCache = {
        plans: Plan[];
        oneTimeProducts: OneTimeProduct[];
        addons: AddOn[];
        subscriptionAddOns: SubscriptionAddOn[];
        planOptionals: PlanOptionals[];
    };
    const [productsCache, setProductsCache] = useState<{ [key: string]: ProductsCache }>({});

    // Helper function to generate cache key from partner and category
    const getCacheKey = (partnerId: string, categoryId: string) => {
        return `${partnerId || 'no-partner'}-${categoryId || 'no-category'}`;
    };

    // Helper function to filter products based on parent_plans
    const filterProductsByParentPlans = (products: any[], planId: number | string) => {
        if (!products || products.length === 0) return [];
        return products.filter((product: any) => {
            const parentPlans = product.parent_plans || [];
            // If no parent_plans specified, show with all plans (no restrictions)
            if (!parentPlans || parentPlans.length === 0) {
                return true;
            }
            // If parent_plans specified, only show if this plan is in the list
            return parentPlans.includes(Number(planId));
        });
    };

    // Helper function to process response data
    const processResponseData = (response: any) => {
        // Helper function to ensure pricing_type defaults to 'fixed' for backward compatibility
        const ensurePricingType = (product: any) => ({
            ...product,
            pricing_type: product.pricing_type || 'fixed'
        });

        const plans = response.Plan || [];
        const planOptionalsWithDefault = (optionals: any[], planId: number | string) => {
            // Filter optionals based on parent_plans first
            const filteredOptionals = filterProductsByParentPlans(optionals, planId);
            const groups = Array.from(new Set(filteredOptionals.map(o => o.group)));
            return groups.flatMap(group => {
                const groupOptions = filteredOptionals.filter(o => o.group === group);
                let selectedIdx = groupOptions.findIndex(o => parseFloat(o.price) === 0);
                if (selectedIdx === -1) selectedIdx = 0; // fallback: select first if no price 0
                return groupOptions.map((o, idx) => ({
                    ...ensurePricingType(o),
                    selected: idx === selectedIdx
                }));
            });
        };
        const processedPlans = plans.map((plan: any) => ({
            ...ensurePricingType(plan),
            addons: filterProductsByParentPlans(response.AddOn || [], plan.id).map((a: any) => ensurePricingType(a)),
            subscriptionAddOns: filterProductsByParentPlans(response.SubscriptionAddOn || [], plan.id).map((a: any) => ensurePricingType(a)),
            optionals: planOptionalsWithDefault(response.PlanOptionals || [], plan.id)
        }));
        return {
            plans: processedPlans,
            oneTimeProducts: (response.OneTimeProduct || []).map((product: any) => ensurePricingType(product)),
            addons: (response.AddOn || []).map((a: any) => ensurePricingType(a)),
            subscriptionAddOns: (response.SubscriptionAddOn || []).map((a: any) => ensurePricingType(a)),
            planOptionals: (response.PlanOptionals || []).map((o: any) => ensurePricingType(o))
        };
    };

    const fetchPackages = async (categoryOverride?: string, partnerOverride?: string) => {
        try {
            // Use the appropriate category based on active tab
            let cat = categoryOverride;
            if (!cat) {
                // If no override, use category based on active tab
                cat = activeTab === '2' ? selectedOnetimeCategoryId : selectedCategoryId;
            }
            const partner = partnerOverride ?? selectedPartnerId;

            // Generate cache key: partner + category (domain is constant for the page)
            const cacheKey = getCacheKey(partner || '', cat || '');

            // Check cache first
            if (productsCache[cacheKey]) {
                const cached = productsCache[cacheKey];
                setPlansState(cached.plans);
                setOneTimeProducts(cached.oneTimeProducts);
                setshowloader(false);
                return;
            }

            // Cache miss - fetch from backend
            setshowloader(true);
            const params: any = { "domain": domain };
            if (cat) params.category = cat;
            if (partner) params.partner = partner;

            const response = await axios.get(
                "/payments/listDomainProducts",
                { params },
                { withCredentials: true }
            );
            if (response && response.status === "SENT") {
                const processedData = processResponseData(response);

                // Update state
                setPlansState(processedData.plans);
                setOneTimeProducts(processedData.oneTimeProducts);

                // Store in cache
                setProductsCache(prev => ({
                    ...prev,
                    [cacheKey]: processedData
                }));
            } else {
                console.log(response);
            }
            setshowloader(false);
        } catch (error: any) {
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    const errorMsg = error.response.data?.error || "Unable to load products. Please try again.";
                    setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Your session has expired. Please log in again.", type: "error" });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else if (error.response.status === 404) {
                    const errorMsg = error.response.data?.error || "The requested information could not be found.";
                    setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to load products. Please try again later.", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to connect to the server. Please check your internet connection.", type: "error" });
            }
            setshowloader(false);
        }
    }

    const fetchCategories = async (partnerId?: string | null, isInitialLoad: boolean = false) => {
        try {
            const params: any = { domain };
            const partner = partnerId ?? selectedPartnerId;
            // Only pass partner if it's explicitly provided (not initial load)
            // If partner is not sent, backend will auto-select first category with products
            if (!isInitialLoad && partner) {
                params.partner = partner;
            }

            const response = await axios.get(
                "/payments/listDomainCategories",
                { params },
                { withCredentials: true }
            );
            if (response && response.status === "SENT") {
                const cats = response.categories || [];
                const onetimeCats = response.onetime_categories || [];
                setCategories(cats);
                setOnetimeCategories(onetimeCats);
                // Backend will return categories with the one having products first (if partner not provided)
                // So we just select the first category for plans
                const firstCat = cats[0];
                const firstCatId = firstCat?.id ? String(firstCat.id) : "";
                setSelectedCategoryId(firstCatId);
                // Select first category for one-time products if available
                const firstOnetimeCat = onetimeCats[0];
                const firstOnetimeCatId = firstOnetimeCat?.id ? String(firstOnetimeCat.id) : "";
                setSelectedOnetimeCategoryId(firstOnetimeCatId);
                // Load packages for the first category (which will have products if partner not provided)
                await fetchPackages(firstCatId, partner);
            }
        } catch (error: any) {
            // Fallback: load packages without category filter (backend defaults to Default for non-admin)
            const partner = partnerId ?? selectedPartnerId;
            await fetchPackages("", partner);
        }
    };

    const fetchPartners = async () => {
        try {
            const response = await axios.get(
                "/payments/listDomainPartners",
                { params: { domain }, withCredentials: true }
            );
            if (response && response.status === "SENT") {
                const partnersList = response.partners || [];
                setPartners(partnersList);
                // Set first partner as default if available and no partner is currently selected
                if (partnersList.length > 0) {
                    const firstPartnerId = String(partnersList[0].id);
                    setSelectedPartnerId(firstPartnerId);
                    return firstPartnerId;
                } else {
                    // Clear plans if no partners available
                    setPlansState([]);
                    setSelectedPartnerId("");
                }
            } else {
                setPartners([]);
                setPlansState([]);
                setSelectedPartnerId("");
            }
        } catch (error: any) {
            setPartners([]);
            setPlansState([]);
            setSelectedPartnerId("");
        }
        return null;
    };

    const fetchPartnerDetails = async (partnerId: string) => {
        if (!partnerId) return;
        try {
            setLoadingPartnerDetails(true);
            const response = await axios.get(
                "/payments/getPartnerDetails/",
                {
                    params: { partner_id: partnerId },
                    withCredentials: true
                }
            );
            if (response && response.status === "SENT") {
                setSelectedPartnerDetails(response.partner);
                setPartnerDetailsModal(true);
            } else {
                setAlertMsgg({ open: true, type: "error", content: "Partner details not found." });
            }
        } catch (error: any) {
            console.error("Error fetching partner details:", error);
            const errorMsg = error.response?.data?.error || "Failed to load partner details.";
            setAlertMsgg({ open: true, type: "error", content: errorMsg });
        } finally {
            setLoadingPartnerDetails(false);
        }
    };

    const handleKnowMoreClick = () => {
        if (selectedPartnerId) {
            fetchPartnerDetails(selectedPartnerId);
        } else {
            setAlertMsgg({ open: true, type: "error", content: "Please select a partner first." });
        }
    };

    // Navigation state for plans
    const [plansStartIndex, setPlansStartIndex] = useState(0);
    const PLANS_VISIBLE_COUNT = 3;

    // Load cart from localStorage on mount
    useEffect(() => {
        const savedCart = localStorage.getItem('oneTimeProductsCart');
        if (savedCart) {
            try {
                setCart(JSON.parse(savedCart));
            } catch (e) {
                console.error('Error loading cart from localStorage', e);
            }
        }
    }, []);

    // Fetch checkout disclaimer on mount
    useEffect(() => {
        fetchCheckoutDisclaimer().then(setCheckoutDisclaimer);
    }, []);

    // Save cart to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('oneTimeProductsCart', JSON.stringify(cart));
    }, [cart]);

    const handleAddonToggle = (planIdx: number, addonIdx: number) => {
        setPlansState(prev => prev.map((plan, i) =>
            i === planIdx ? {
                ...plan,
                addons: plan.addons.map((a, j) => j === addonIdx ? { ...a, selected: !a.selected } : a)
            } : plan
        ));
    };

    const handleOptionSelect = (planIdx: number, group: string, optionIdx: number) => {
        setPlansState(prev => prev.map((plan, i) =>
            i === planIdx ? {
                ...plan,
                optionals: plan.optionals.map((o, j) =>
                    o.group === group ? { ...o, selected: j === optionIdx } : o
                )
            } : plan
        ));
    };

    const handleSubscriptionAddonToggle = (planIdx: number, addonIdx: number) => {
        setPlansState(prev => prev.map((plan, i) =>
            i === planIdx ? {
                ...plan,
                subscriptionAddOns: (plan.subscriptionAddOns || []).map((a, j) => j === addonIdx ? { ...a, selected: !a.selected } : a)
            } : plan
        ));
    };

    const openProductDetailsModal = (product: {
        type: 'addon' | 'subscriptionAddon' | 'optional';
        title: string;
        price: string | null;
        description: string;
        features: string[];
        period?: string;
        validity?: string;
    }) => {
        setSelectedProductDetails(product);
        setProductDetailsModal(true);
    };

    const addToCart = (product: OneTimeProduct) => {
        // Check if product has external link - if so, redirect directly instead of adding to cart
        const pricingType = product.pricing_type || 'fixed';
        if (pricingType === 'external' && product.external_url) {
            window.open(product.external_url, '_blank');
            setAlertMsgg({ open: true, content: `Opening external purchase page.`, type: "success" });
            setTimeout(() => setAlertMsgg(prev => ({ ...prev, open: false })), 3000);
            return;
        }

        setCart(prev => {
            const existing = prev.find(item => item.product.id === product.id);
            if (existing) {
                return prev.map(item =>
                    item.product.id === product.id
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...prev, { product, quantity: 1 }];
        });
        setAlertMsgg({ open: true, content: `${product.title} added to cart!`, type: "success" });
        setTimeout(() => setAlertMsgg(prev => ({ ...prev, open: false })), 2000);
    };

    const updateCartQuantity = (productId: string, change: number) => {
        setCart(prev => {
            return prev.map(item => {
                if (item.product.id === productId) {
                    const newQuantity = item.quantity + change;
                    return newQuantity > 0 ? { ...item, quantity: newQuantity } : item;
                }
                return item;
            }).filter(item => item.quantity > 0);
        });
    };

    const removeFromCart = (productId: string) => {
        setCart(prev => prev.filter(item => item.product.id !== productId));
    };

    const getCartTotal = () => {
        return cart.reduce((total, item) => {
            const pricingType = item.product.pricing_type || 'fixed';
            // Skip items with contact pricing or null price (unless external)
            if (pricingType === 'contact' || (pricingType !== 'external' && (item.product.price === null || item.product.price === undefined))) {
                return total;
            }
            const price = parseFloat(item.product.price) || 0;
            return total + (price * item.quantity);
        }, 0).toFixed(2);
    };

    const getCartItemCount = () => {
        return cart.reduce((count, item) => count + item.quantity, 0);
    };

    const fetchActivePlan = async () => {
        try {
            const response = await axios.get("/payments/active-plan-for-domain/", {
                params: { domain },
                withCredentials: true,
            });
            if (response && response.status === "SENT" && response.activePlan) {
                setActivePlan(response.activePlan);
            } else {
                setActivePlan(null);
            }
        } catch {
            setActivePlan(null);
        }
    };

    useEffect(() => {
        // Clear cache when domain changes
        setProductsCache({});
        setActivePlan(null);
        const initializeData = async () => {
            fetchActivePlan();
            const defaultPartnerId = await fetchPartners();
            // After partners are loaded, fetch categories which will trigger package fetch with the default partner
            // Pass isInitialLoad=true so backend auto-selects first category with products
            // Always call fetchCategories, even if there are no partners (it will handle empty partner filter)
            await fetchCategories(defaultPartnerId || undefined, true);
        };
        initializeData();
    }, [domain]);

    // Navigation functions for plans
    const navigatePlansLeft = () => {
        setPlansStartIndex(Math.max(0, plansStartIndex - 1));
    };
    const navigatePlansRight = () => {
        const maxStartIndex = Math.max(0, plansState.length - PLANS_VISIBLE_COUNT);
        setPlansStartIndex(Math.min(maxStartIndex, plansStartIndex + 1));
    };
    const getVisiblePlans = () => {
        if (plansState.length <= PLANS_VISIBLE_COUNT) {
            return plansState;
        }
        return plansState.slice(plansStartIndex, plansStartIndex + PLANS_VISIBLE_COUNT);
    };

    const getVisiblePlansWithPlaceholders = (): (Plan | null)[] => {
        const visiblePlans = getVisiblePlans();
        // For 1 plan: put plan in center column [null, plan, null]
        if (visiblePlans.length === 1) {
            return [null, visiblePlans[0], null];
        }
        // For 2 plans: [plan, plan, null] - left align
        const plansToShow: (Plan | null)[] = [...visiblePlans];
        while (plansToShow.length < 3) {
            plansToShow.push(null);
        }
        return plansToShow.slice(0, 3);
    };

    // Reset navigation index when plans change
    useEffect(() => {
        if (plansStartIndex >= plansState.length && plansState.length > 0) {
            setPlansStartIndex(Math.max(0, plansState.length - PLANS_VISIBLE_COUNT));
        } else if (plansState.length <= PLANS_VISIBLE_COUNT) {
            setPlansStartIndex(0);
        }
    }, [plansState.length]);

    const isCurrentPlan = (plan: Plan): boolean => {
        if (!activePlan) return false;
        return (
            String(plan.id) === activePlan.planId ||
            (plan.product_group_id != null && plan.product_group_id === activePlan.productGroupId)
        );
    };

    // Check purchase agreements for a partner
    const checkPurchaseAgreements = async (partnerId: string): Promise<boolean> => {
        if (!partnerId) return false;
        try {
            setIsLoadingAgreements(true);
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/payments/check-purchase-agreements/`, {
                params: { partner_id: partnerId }
            });
            if (response.status === "SENT") {
                const agreements = response.agreements || [];
                const requiredIds = response.required_agreement_ids || [];
                setPurchaseAgreements(agreements);
                setRequiredAgreementIds(requiredIds);
                setAcceptedAgreementIds([]);
                return agreements.length > 0;
            }
            return false;
        } catch (error: any) {
            console.error("Error checking purchase agreements:", error);
            return false;
        } finally {
            setIsLoadingAgreements(false);
        }
    };

    // Proceed with checkout after agreements are accepted
    const proceedWithCheckout = async () => {
        if (!pendingCheckoutData) return;

        try {
            setshowloader(true);
            setPurchaseAgreementModal(false);

            // Create checkout and get order_id
            const checkoutPayload = {
                ...pendingCheckoutData.payload,
                ...(checkoutDisclaimer ? { disclaimer_version_id: checkoutDisclaimer.id } : {}),
            };
            const response = await axios.post('payments/create_package_checkout', checkoutPayload, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.status === 'external_link') {
                window.open(response.external_url, '_blank');
                setAlertMsgg({ ...alertMsgg, open: true, content: `Opening external purchase page.`, type: "success" });
                setTimeout(() => { setAlertMsgg(prev => ({ ...prev, open: false })); }, 3000);
                setshowloader(false);
                setPendingCheckoutData(null);
            } else if (response.status === 'chekout_stripe') {
                const orderId = response.order_id;

                // Accept purchase agreements with order_id
                if (purchaseAgreements.length > 0 && acceptedAgreementIds.length > 0) {
                    try {
                        await axios.post(`${process.env.REACT_APP_BASE_URL}/payments/accept-purchase-agreements/`, {
                            order_id: orderId,
                            agreement_ids: acceptedAgreementIds
                        }, {
                            withCredentials: true,
                            headers: {
                                "Content-Type": "application/json",
                            },
                        });
                    } catch (acceptError: any) {
                        console.error("Error accepting purchase agreements:", acceptError);
                        // Log error but don't block checkout - agreements are already shown and accepted
                    }
                }

                // Redirect to Stripe
                if (pendingCheckoutData.type === 'plan') {
                    setAlertMsgg({ ...alertMsgg, open: true, content: `Redirecting you for subscription payment.`, type: "success" });
                    setTimeout(() => { setAlertMsgg(prev => ({ ...prev, open: false })); }, 7000);
                    setTimeout(() => {
                        window.location.href = response.success_url;
                        setshowloader(false);
                    }, 8000);
                } else {
                    setAlertMsgg({ open: true, content: `Redirecting you for payment...`, type: "success" });
                    setTimeout(() => {
                        window.location.href = response.success_url;
                        setCart([]);
                        localStorage.removeItem('oneTimeProductsCart');
                        setshowloader(false);
                    }, 2000);
                }
                setPendingCheckoutData(null);
            }
        } catch (error: any) {
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data.error, type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Session expired!", type: "error" });
                    setTimeout(() => { window.location.href = "/logout" }, 2000);
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "An error occurred", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
            setPendingCheckoutData(null);
        }
    };

    const handleSelect = async (planIdx: number) => {
        try {
            const selectedPlan = plansState[planIdx];
            const selectedAddons = selectedPlan.addons.filter(a => a.selected);
            const selectedOptionals = selectedPlan.optionals.filter(o => o.selected);
            const selectedSubscriptionAddOns = (selectedPlan.subscriptionAddOns || []).filter(a => a.selected);
            const totalPrice = getPlanTotal(selectedPlan);
            console.log("Selected plan:", selectedPlan);

            // Check if plan has external link - if so, redirect directly (regardless of addons/optionals)
            const pricingType = selectedPlan.pricing_type || 'fixed';
            if (pricingType === 'external' && selectedPlan.external_url) {
                window.open(selectedPlan.external_url, '_blank');
                setAlertMsgg({ ...alertMsgg, open: true, content: `Opening external purchase page.`, type: "success" })
                setTimeout(() => { setAlertMsgg(prev => ({ ...prev, open: false })); }, 3000);
                return;
            }

            const payload = {
                cart: [selectedPlan, ...selectedAddons, ...selectedOptionals, ...selectedSubscriptionAddOns],
                total_price: totalPrice,
            };

            // Check for purchase agreements or disclaimer before proceeding
            const hasAgreements = await checkPurchaseAgreements(selectedPartnerId);
            if (hasAgreements || checkoutDisclaimer) {
                // Show modal and store checkout data
                setPendingCheckoutData({ type: 'plan', planIdx, payload });
                setDisclaimerAccepted(false);
                setPurchaseAgreementModal(true);
                return;
            }

            // No agreements or disclaimer, proceed directly with checkout
            setshowloader(true);
            const response = await axios.post('payments/create_package_checkout', { ...payload }, {
                withCredentials: true, headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.status === 'external_link') {
                // External link from backend (for one-time products or fallback)
                window.open(response.external_url, '_blank');
                setAlertMsgg({ ...alertMsgg, open: true, content: `Opening external purchase page.`, type: "success" })
                setTimeout(() => { setAlertMsgg(prev => ({ ...prev, open: false })); }, 3000);
                setshowloader(false)
            } else if (response.status === 'chekout_stripe') {
                console.log('Sucess stripe payment')
                setAlertMsgg({ ...alertMsgg, open: true, content: `Redirecting you for subscription payment.`, type: "success" })
                setTimeout(() => { setAlertMsgg(prev => ({ ...prev, open: false })); }, 7000);
                setTimeout(() => {
                    window.location.href = response.success_url;
                    setshowloader(false)
                }, 8000);
            }
        } catch (error: any) {
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    console.log('Error in stripe payment')
                    setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data.error, type: "error" });
                }
                else if (error.response.status === 403) {
                    console.log('Error stripe payment')
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Session expired!", type: "error" });
                    setTimeout(() => { window.location.href = "/logout" }, 2000)
                } else {
                    console.log('An error occurred')
                    setAlertMsgg({ ...alertMsgg, open: true, content: "An error occurred", type: "error" });
                }
            } else {
                console.log('An error occurred')
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false)
        }
    };

    const handleCheckout = async () => {
        if (cart.length === 0) {
            setAlertMsgg({ open: true, content: "Your cart is empty!", type: "error" });
            return;
        }

        // Check if any product in cart has external link
        const externalProduct = cart.find(item => {
            const pricingType = item.product.pricing_type || 'fixed';
            return pricingType === 'external' && item.product.external_url;
        });
        if (externalProduct) {
            // If multiple products, use the first external link found
            window.open(externalProduct.product.external_url, '_blank');
            setAlertMsgg({ open: true, content: `Opening external purchase page.`, type: "success" });
            setTimeout(() => { setAlertMsgg(prev => ({ ...prev, open: false })); }, 3000);
            return;
        }

        const payload = {
            cart: cart.map(item => ({
                id: item.product.id,
                product_type: 'onetimeproduct',
                quantity: item.quantity
            })),
            total_price: getCartTotal(),
        };

        // Check for purchase agreements or disclaimer before proceeding
        const hasAgreements = await checkPurchaseAgreements(selectedPartnerId);
        if (hasAgreements || checkoutDisclaimer) {
            // Show modal and store checkout data
            setPendingCheckoutData({ type: 'cart', payload });
            setDisclaimerAccepted(false);
            setPurchaseAgreementModal(true);
            return;
        }

        // No agreements or disclaimer, proceed directly with checkout
        try {
            setshowloader(true);
            const response = await axios.post('payments/create_package_checkout', { ...payload }, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.status === 'external_link') {
                // External link from backend
                window.open(response.external_url, '_blank');
                setAlertMsgg({ open: true, content: `Opening external purchase page.`, type: "success" });
                setTimeout(() => { setAlertMsgg(prev => ({ ...prev, open: false })); }, 3000);
                setshowloader(false);
            } else if (response.status === 'chekout_stripe') {
                setAlertMsgg({ open: true, content: `Redirecting you for payment...`, type: "success" });
                setTimeout(() => {
                    window.location.href = response.success_url;
                    // Clear cart after successful checkout
                    setCart([]);
                    localStorage.removeItem('oneTimeProductsCart');
                }, 2000);
            }
            setshowloader(false);
        } catch (error: any) {
            if (error.response) {
                setAlertMsgg({ open: true, content: error.response.data?.error || "Checkout failed", type: "error" });
            } else {
                setAlertMsgg({ open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };

    // Helper function to convert text to title case
    const toTitleCase = (str: string) => {
        if (!str) return '';
        return str.toLowerCase().split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    // Helper function to clean markdown content
    const cleanMarkdown = (text: string | null | undefined): string => {
        if (!text) return '';
        // Convert single newlines to double newlines for markdown paragraph breaks
        return String(text).replace(/\n/g, '\n\n');
    };

    // Get selected partner name
    const getSelectedPartnerName = () => {
        if (!selectedPartnerId) return '';
        const partner = partners.find(p => String(p.id) === selectedPartnerId);
        return partner ? toTitleCase(partner.name || '') : '';
    };

    const fetchCommPreferences = async (partnerId: string) => {
        if (!partnerId) {
            setCommPreferences(null);
            return;
        }
        try {
            setLoadingCommPrefs(true);
            const response = await axios.get(
                "/payments/getPartnerCommunicationPreferences",
                { params: { partner_id: partnerId } },
                { withCredentials: true }
            );
            if (response && response.status === "SENT") {
                setCommPreferences(response.communication_preferences);
            }
        } catch (error: any) {
            console.error("Error fetching communication preferences:", error);
            setCommPreferences(null);
        } finally {
            setLoadingCommPrefs(false);
        }
    };

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
            // Fetch communication preferences when "Sales support" tab is clicked
            if (tab === '3' && selectedPartnerId) {
                fetchCommPreferences(selectedPartnerId);
            }
            // Fetch packages when switching between Plans and One-Time Products tabs
            if (tab === '1' || tab === '2') {
                const categoryId = tab === '2' ? selectedOnetimeCategoryId : selectedCategoryId;
                fetchPackages(categoryId, selectedPartnerId);
            }
        }
    };

    // Fetch communication preferences when partner changes and "Sales support" tab is active
    useEffect(() => {
        if (activeTab === '3' && selectedPartnerId) {
            fetchCommPreferences(selectedPartnerId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPartnerId, activeTab]);

    return (
        <div>
            {alertMsgg.open && (
                <Alert
                    color={alertMsgg.type === "success" ? "success" : "danger"}
                    toggle={handleAlertMsggClose}
                    style={{ fontWeight: "bold" }}
                >
                    {alertMsgg.content}
                </Alert>
            )}

            <Breadcrumbs title="Plans" breadcrumbItem="Packages" />

            {activePlan && (
                <div style={{
                    marginBottom: 24,
                    borderRadius: 12,
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    border: '1px solid #e8e8e8',
                    background: '#fff',
                    padding: '14px 24px',
                    width: '50%',
                }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Your current plan
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 20, color: '#222', marginBottom: 6 }}>
                        {activePlan.planTitle}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px', fontSize: 13, color: '#666', marginBottom: 6 }}>
                        {activePlan.purchasedAt && (
                            <span>
                                Purchased: {new Date(activePlan.purchasedAt + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                        )}
                        {activePlan.expiresAt && (
                            <span>
                                {activePlan.status === 'active' ? 'Renewal' : 'End'} date: {new Date(activePlan.expiresAt + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                        )}
                    </div>
                    <Badge
                        style={{
                            background: activePlan.status === 'active' ? '#2e7d32' : '#757575',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: 11,
                            padding: '4px 10px',
                        }}
                    >
                        {activePlan.status === 'active' ? 'Active' : 'Cancelling'}
                    </Badge>
                </div>
            )}

            {((activeTab === '2' && onetimeCategories.length > 0) || (activeTab !== '2' && categories.length > 0)) && (
                <div className="d-flex justify-content-end mb-3">

                    {partners.length > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginRight: 12 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>Partner</span>
                            <select
                                className="form-control"
                                style={{ minWidth: 220 }}
                                value={selectedPartnerId}
                                onChange={(e) => {
                                    const next = e.target.value;
                                    setSelectedPartnerId(next);
                                    fetchPackages(undefined, next);
                                }}
                            >
                                {partners.map((p: any) => (
                                    <option key={p.id} value={String(p.id)}>
                                        {toTitleCase(p.name || `Partner #${p.id}`)}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={handleKnowMoreClick}
                                disabled={!selectedPartnerId || loadingPartnerDetails}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: "#0d6efd",
                                    cursor: selectedPartnerId && !loadingPartnerDetails ? "pointer" : "not-allowed",
                                    textDecoration: "underline",
                                    fontSize: "13px",
                                    padding: "4px 8px",
                                    opacity: selectedPartnerId && !loadingPartnerDetails ? 1 : 0.5
                                }}
                            >
                                {loadingPartnerDetails ? "Loading..." : "Know more about partner"}
                            </button>
                        </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>Category</span>
                        <select
                            className="form-control"
                            style={{ minWidth: 220 }}
                            value={activeTab === '2' ? selectedOnetimeCategoryId : selectedCategoryId}
                            onChange={(e) => {
                                const next = e.target.value;
                                if (activeTab === '2') {
                                    setSelectedOnetimeCategoryId(next);
                                } else {
                                    setSelectedCategoryId(next);
                                }
                                fetchPackages(next, selectedPartnerId);
                            }}
                        >
                            {(activeTab === '2' ? onetimeCategories : categories).map((cat: any) => (
                                <option key={cat.id} value={String(cat.id)}>
                                    {cat.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            <div className="loaderContainer" hidden={!showloader}>
                <div className="spinner">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                </div>
            </div>

            {/* Tabs Navigation */}
            <Nav tabs className="nav-tabs-custom mb-4">
                <NavItem>
                    <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({ active: activeTab === '1' })}
                        onClick={() => { toggleTab('1'); }}
                    >
                        <span className="d-block d-sm-none"><i className="fas fa-home"></i></span>
                        <span className="d-none d-sm-block">Plans</span>
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({ active: activeTab === '2' })}
                        onClick={() => { toggleTab('2'); }}
                    >
                        <span className="d-block d-sm-none"><i className="fas fa-cog"></i></span>
                        <span className="d-none d-sm-block">Products & Services</span>
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({ active: activeTab === '3' })}
                        onClick={() => { toggleTab('3'); }}
                    >
                        <span className="d-block d-sm-none"><i className="fas fa-info-circle"></i></span>
                        <span className="d-none d-sm-block">Sales support</span>
                    </NavLink>
                </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
                {/* Plans Tab */}
                <TabPane tabId="1">
                    {!showloader && partners.length === 0 && (
                        <div className="text-center text-muted" style={{ padding: '24px 12px' }}>
                            Plans not available at this moment.
                        </div>
                    )}

                    {!showloader && partners.length > 0 && plansState.length === 0 && (
                        <div className="text-center text-muted" style={{ padding: '24px 12px' }}>
                            No plans are available for this filter.
                        </div>
                    )}

                    {!showloader && partners.length > 0 && plansState.length > 0 && (
                        <>
                            {plansState.length > PLANS_VISIBLE_COUNT && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16, gap: 8 }}>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={navigatePlansLeft}
                                        disabled={plansStartIndex === 0}
                                        style={{ minWidth: 40, padding: '6px 12px' }}
                                    >
                                        <i className="bx bx-chevron-left" style={{ fontSize: 20 }}></i>
                                    </button>
                                    <span style={{ fontSize: 14, color: '#666', minWidth: 80, textAlign: 'center' }}>
                                        {plansStartIndex + 1}-{Math.min(plansStartIndex + PLANS_VISIBLE_COUNT, plansState.length)} of {plansState.length}
                                    </span>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={navigatePlansRight}
                                        disabled={plansStartIndex >= plansState.length - PLANS_VISIBLE_COUNT}
                                        style={{ minWidth: 40, padding: '6px 12px' }}
                                    >
                                        <i className="bx bx-chevron-right" style={{ fontSize: 20 }}></i>
                                    </button>
                                </div>
                            )}
                            <div style={{ width: '100%', overflowX: 'auto', marginTop: 20 }}>
                                <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 180, border: '1px solid #e0e0e0', background: 'transparent' }}></th>
                                            {getVisiblePlansWithPlaceholders().map((plan, visibleIdx) => {
                                                if (!plan) {
                                                    return (
                                                        <th key={`empty-${visibleIdx}`} style={{ width: 'calc((100% - 180px) / 3)', padding: 24, border: '1px solid #e0e0e0', textAlign: 'center', background: 'transparent' }}>
                                                        </th>
                                                    );
                                                }
                                                const planIdx = plansState.length === 1 ? 0 : plansStartIndex + visibleIdx;
                                                return (
                                                    <th key={plan.id} style={{ width: 'calc((100% - 180px) / 3)', padding: 24, border: '1px solid #e0e0e0', textAlign: 'center', background: 'transparent' }}>
                                                        <div style={{ fontWeight: 700, fontSize: 20, color: '#222', marginBottom: 4 }}>{plan.title}</div>
                                                        <div style={{ fontSize: 13, color: '#555', marginBottom: 4, position: 'relative', fontWeight: 'normal' }}>
                                                            {plan.description && plan.description.length > 50 ? (
                                                                <>
                                                                    <span>{plan.description.substring(0, 50).replace(/\*\*/g, '').replace(/\*/g, '')}</span>
                                                                    <span
                                                                        style={{ color: '#1976d2', cursor: 'pointer' }}
                                                                        onMouseEnter={e => {
                                                                            const tip = document.getElementById(`plan-desc-tip-${planIdx}`);
                                                                            if (tip) tip.style.display = 'block';
                                                                        }}
                                                                        onMouseLeave={e => {
                                                                            const tip = document.getElementById(`plan-desc-tip-${planIdx}`);
                                                                            if (tip) tip.style.display = 'none';
                                                                        }}
                                                                    >
                                                                        {' '}...more
                                                                    </span>
                                                                    <div
                                                                        id={`plan-desc-tip-${planIdx}`}
                                                                        style={{
                                                                            display: 'none',
                                                                            position: 'absolute',
                                                                            top: '100%',
                                                                            left: '50%',
                                                                            transform: 'translateX(-50%)',
                                                                            marginTop: 8,
                                                                            background: '#222',
                                                                            color: '#fff',
                                                                            padding: 10,
                                                                            borderRadius: 8,
                                                                            fontSize: 12,
                                                                            zIndex: 10,
                                                                            minWidth: 200,
                                                                            maxWidth: 300,
                                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                                            whiteSpace: 'normal',
                                                                            wordBreak: 'break-word',
                                                                        }}
                                                                    >
                                                                        <ReactMarkdown
                                                                            components={{
                                                                                p: ({ children }) => <p style={{ marginBottom: '6px', marginTop: 0, fontSize: 12, color: '#fff' }}>{children}</p>,
                                                                                strong: ({ children }) => <strong style={{ fontWeight: 600, color: '#fff' }}>{children}</strong>,
                                                                                em: ({ children }) => <em style={{ fontStyle: 'italic', color: '#fff' }}>{children}</em>,
                                                                                ul: ({ children }) => <ul style={{ marginBottom: '6px', paddingLeft: '16px', fontSize: 12, color: '#fff' }}>{children}</ul>,
                                                                                ol: ({ children }) => <ol style={{ marginBottom: '6px', paddingLeft: '16px', fontSize: 12, color: '#fff' }}>{children}</ol>,
                                                                                li: ({ children }) => <li style={{ marginBottom: '2px', fontSize: 12, color: '#fff' }}>{children}</li>,
                                                                                a: ({ children, href }) => <a href={href} style={{ color: '#4fc3f7', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                                                            }}
                                                                        >
                                                                            {cleanMarkdown(plan.description)}
                                                                        </ReactMarkdown>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <ReactMarkdown
                                                                    components={{
                                                                        p: ({ children }) => <span style={{ fontSize: 13, color: '#555', fontWeight: 'normal' }}>{children}</span>,
                                                                        strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                                                        em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                                        ul: ({ children }) => <ul style={{ margin: 0, paddingLeft: '16px', fontSize: 13, color: '#555', display: 'inline' }}>{children}</ul>,
                                                                        ol: ({ children }) => <ol style={{ margin: 0, paddingLeft: '16px', fontSize: 13, color: '#555', display: 'inline' }}>{children}</ol>,
                                                                        li: ({ children }) => <li style={{ fontSize: 13, color: '#555' }}>{children}</li>,
                                                                        a: ({ children, href }) => <a href={href} style={{ color: '#1976d2', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                                                    }}
                                                                >
                                                                    {cleanMarkdown(plan.description)}
                                                                </ReactMarkdown>
                                                            )}
                                                        </div>
                                                        <div style={{ fontWeight: 700, fontSize: 28, color: '#1976d2', marginBottom: 0 }}>
                                                            {(() => {
                                                                const pricingType = plan.pricing_type || 'fixed';
                                                                if (pricingType === 'contact') {
                                                                    return (
                                                                        <span
                                                                            onClick={() => {
                                                                                setActiveTab('3');
                                                                                // Scroll to Sales Support section
                                                                                setTimeout(() => {
                                                                                    const salesSupportTab = document.querySelector('[tabid="3"]');
                                                                                    if (salesSupportTab) {
                                                                                        salesSupportTab.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                                                    }
                                                                                }, 100);
                                                                            }}
                                                                            style={{ fontSize: 16, fontWeight: 500, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                                                                        >
                                                                            Contact for pricing
                                                                        </span>
                                                                    );
                                                                } else if (pricingType === 'external') {
                                                                    // For external, only show price if it exists
                                                                    return plan.price !== null && plan.price !== undefined ? (
                                                                        <>
                                                                            ${formatPrice(plan.price)}
                                                                            {plan.validity && <span style={{ fontSize: 16, fontWeight: 500, color: '#666', marginLeft: 4 }}> / {plan.validity}</span>}
                                                                        </>
                                                                    ) : null;
                                                                } else {
                                                                    // Fixed pricing - show price or contact if null
                                                                    return plan.price !== null && plan.price !== undefined ? (
                                                                        <>
                                                                            ${formatPrice(plan.price)}
                                                                            {plan.validity && <span style={{ fontSize: 16, fontWeight: 500, color: '#666', marginLeft: 4 }}> / {plan.validity}</span>}
                                                                        </>
                                                                    ) : (
                                                                        <span
                                                                            onClick={() => {
                                                                                setActiveTab('3');
                                                                                setTimeout(() => {
                                                                                    const salesSupportTab = document.querySelector('[tabid="3"]');
                                                                                    if (salesSupportTab) {
                                                                                        salesSupportTab.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                                                    }
                                                                                }, 100);
                                                                            }}
                                                                            style={{ fontSize: 16, fontWeight: 500, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                                                                        >
                                                                            Contact for pricing
                                                                        </span>
                                                                    );
                                                                }
                                                            })()}
                                                        </div>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Features Row */}
                                        <tr>
                                            <td style={{ fontWeight: 600, fontSize: 15, color: '#222', background: '#fafbfc', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle' }}>Features</td>
                                            {getVisiblePlansWithPlaceholders().map((plan, visibleIdx) => {
                                                if (!plan) {
                                                    return (
                                                        <td key={`empty-features-${visibleIdx}`} style={{ padding: 16, verticalAlign: 'top', background: '#fafbfc', border: '1px solid #e0e0e0' }}>
                                                        </td>
                                                    );
                                                }
                                                const planIdx = plansState.length === 1 ? 0 : plansStartIndex + visibleIdx;
                                                return (
                                                    <td key={plan.id + '-features'} style={{ padding: 16, verticalAlign: 'top', background: '#fafbfc', border: '1px solid #e0e0e0' }}>
                                                        <ul style={{ listStyle: 'disc', paddingLeft: 18, margin: 0, fontSize: 13, color: '#444' }}>
                                                            {plan.features.map((f, i) => <li key={i}>{f}</li>)}
                                                        </ul>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                        {/* Add-ons Row */}
                                        {getVisiblePlans().some(plan => plan && plan.addons && plan.addons.length > 0) && (
                                            <tr>
                                                <td style={{ fontWeight: 600, fontSize: 15, color: '#222', background: '#fff', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle' }}>Add-ons</td>
                                                {getVisiblePlansWithPlaceholders().map((plan, visibleIdx) => {
                                                    if (!plan) {
                                                        return (
                                                            <td key={`empty-addons-${visibleIdx}`} style={{ padding: 16, background: '#fff', border: '1px solid #e0e0e0' }}>
                                                            </td>
                                                        );
                                                    }
                                                    const planIdx = plansState.length === 1 ? 0 : plansStartIndex + visibleIdx;
                                                    return (
                                                        <td key={plan.id + '-addons'} style={{ padding: 16, background: '#fff', border: '1px solid #e0e0e0' }}>
                                                            {(() => {
                                                                const planPricingType = plan.pricing_type || 'fixed';
                                                                if (planPricingType === 'contact') {
                                                                    return <div style={{ fontSize: 13, color: '#888', textAlign: 'center' }}>—</div>;
                                                                }
                                                                return (
                                                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                                        {plan.addons.map((addon, addonIdx) => {
                                                                            const addonPricingType = addon.pricing_type || 'fixed';
                                                                            const showPrice = addon.price !== null && addon.price !== undefined;
                                                                            const priceText = showPrice ? `$${formatPrice(addon.price)}` : (addonPricingType === 'contact' ? 'Contact' : '');
                                                                            return (
                                                                                <div key={addon.id} style={{ position: 'relative', marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={addon.selected}
                                                                                        onChange={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleAddonToggle(planIdx, addonIdx);
                                                                                        }}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        style={{
                                                                                            width: '16px',
                                                                                            height: '16px',
                                                                                            cursor: 'pointer',
                                                                                            margin: 0,
                                                                                            flexShrink: 0
                                                                                        }}
                                                                                    />
                                                                                    <span
                                                                                        onClick={() => openProductDetailsModal({
                                                                                            type: 'addon',
                                                                                            title: addon.title,
                                                                                            price: addon.price,
                                                                                            description: addon.description || '',
                                                                                            features: addon.features || []
                                                                                        })}
                                                                                        style={{
                                                                                            padding: '4px 10px',
                                                                                            borderRadius: 16,
                                                                                            background: addon.selected ? '#f5f5f5' : '#fafbfc',
                                                                                            color: '#222',
                                                                                            cursor: 'pointer',
                                                                                            border: addon.selected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                                                                            fontWeight: 500,
                                                                                            fontSize: 13,
                                                                                            transition: 'all 0.2s',
                                                                                            display: 'inline-block',
                                                                                            minWidth: 0
                                                                                        }}
                                                                                    >
                                                                                        {addon.title} {priceText && <span style={{ fontWeight: 400, fontSize: 12 }}>{priceText}</span>}
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                );
                                                            })()}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        )}
                                        {/* Subscription Add-ons Row */}
                                        {getVisiblePlans().some(plan => plan && plan.subscriptionAddOns && plan.subscriptionAddOns.length > 0) && (
                                            <tr>
                                                <td style={{ fontWeight: 600, fontSize: 15, color: '#222', background: '#fff', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle' }}>Subscription Add-ons</td>
                                                {getVisiblePlansWithPlaceholders().map((plan, visibleIdx) => {
                                                    if (!plan) {
                                                        return (
                                                            <td key={`empty-subscription-addons-${visibleIdx}`} style={{ padding: 16, background: '#fff', border: '1px solid #e0e0e0' }}>
                                                            </td>
                                                        );
                                                    }
                                                    const planIdx = plansState.length === 1 ? 0 : plansStartIndex + visibleIdx;
                                                    return (
                                                        <td key={plan.id + '-subscription-addons'} style={{ padding: 16, background: '#fff', border: '1px solid #e0e0e0' }}>
                                                            {(() => {
                                                                const planPricingType = plan.pricing_type || 'fixed';
                                                                if (planPricingType === 'contact') {
                                                                    return <div style={{ fontSize: 13, color: '#888', textAlign: 'center' }}>—</div>;
                                                                }
                                                                return (
                                                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                                        {(plan.subscriptionAddOns || []).map((addon, addonIdx) => {
                                                                            const addonPricingType = addon.pricing_type || 'fixed';
                                                                            const showPrice = addon.price !== null && addon.price !== undefined;
                                                                            const priceText = showPrice ? `$${formatPrice(addon.price)} / ${addon.period || 'month'}` : (addonPricingType === 'contact' ? 'Contact' : '');
                                                                            return (
                                                                                <div key={addon.id} style={{ position: 'relative', marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={addon.selected}
                                                                                        onChange={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleSubscriptionAddonToggle(planIdx, addonIdx);
                                                                                        }}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        style={{
                                                                                            width: '16px',
                                                                                            height: '16px',
                                                                                            cursor: 'pointer',
                                                                                            margin: 0,
                                                                                            flexShrink: 0
                                                                                        }}
                                                                                    />
                                                                                    <span
                                                                                        onClick={() => openProductDetailsModal({
                                                                                            type: 'subscriptionAddon',
                                                                                            title: addon.title,
                                                                                            price: addon.price,
                                                                                            description: addon.description || '',
                                                                                            features: addon.features || [],
                                                                                            period: addon.period
                                                                                        })}
                                                                                        style={{
                                                                                            padding: '4px 10px',
                                                                                            borderRadius: 16,
                                                                                            background: addon.selected ? '#f5f5f5' : '#fafbfc',
                                                                                            color: '#222',
                                                                                            cursor: 'pointer',
                                                                                            border: addon.selected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                                                                            fontWeight: 500,
                                                                                            fontSize: 13,
                                                                                            transition: 'all 0.2s',
                                                                                            display: 'inline-block',
                                                                                            minWidth: 0
                                                                                        }}
                                                                                    >
                                                                                        {addon.title} {priceText && <span style={{ fontWeight: 400, fontSize: 12 }}>{priceText}</span>}
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                );
                                                            })()}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        )}
                                        {/* Options Row */}
                                        {getVisiblePlans().some(plan => plan && plan.optionals && plan.optionals.length > 0) && (
                                            <tr>
                                                <td style={{ fontWeight: 600, fontSize: 15, color: '#222', background: '#fafbfc', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle' }}>Options</td>
                                                {getVisiblePlansWithPlaceholders().map((plan, visibleIdx) => {
                                                    if (!plan) {
                                                        return (
                                                            <td key={`empty-options-${visibleIdx}`} style={{ padding: 16, background: '#fafbfc', border: '1px solid #e0e0e0' }}>
                                                            </td>
                                                        );
                                                    }
                                                    const planIdx = plansState.length === 1 ? 0 : plansStartIndex + visibleIdx;
                                                    return (
                                                        <td key={plan.id + '-options'} style={{ padding: 16, background: '#fafbfc', border: '1px solid #e0e0e0' }}>
                                                            {(() => {
                                                                const planPricingType = plan.pricing_type || 'fixed';
                                                                if (planPricingType === 'contact') {
                                                                    return <div style={{ fontSize: 13, color: '#888', textAlign: 'center' }}>—</div>;
                                                                }
                                                                return (
                                                                    <>
                                                                        {Array.from(new Set(plan.optionals.map(o => o.group))).map(group => (
                                                                            <div key={group} style={{ marginBottom: 8 }}>
                                                                                <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 2, color: '#555' }}>{group}</div>
                                                                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                                                    {plan.optionals.filter(o => o.group === group).map((option, optionIdx) => {
                                                                                        const optionPricingType = option.pricing_type || 'fixed';
                                                                                        const showPrice = option.price !== null && option.price !== undefined;
                                                                                        const priceText = showPrice ? `$${formatPrice(option.price)}${option.validity && option.validity !== '' && option.validity !== 'lifetime' && option.validity !== 'custom' ? ` / ${option.validity}` : ''}` : (optionPricingType === 'contact' ? 'Contact' : '');
                                                                                        return (
                                                                                            <div key={option.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                                                                                <input
                                                                                                    type="radio"
                                                                                                    name={`option-group-${planIdx}-${group}`}
                                                                                                    checked={option.selected}
                                                                                                    onChange={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        handleOptionSelect(planIdx, group, plan.optionals.findIndex(o => o.id === option.id));
                                                                                                    }}
                                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                                    style={{
                                                                                                        width: '16px',
                                                                                                        height: '16px',
                                                                                                        cursor: 'pointer',
                                                                                                        margin: 0,
                                                                                                        flexShrink: 0
                                                                                                    }}
                                                                                                />
                                                                                                <span
                                                                                                    onClick={() => openProductDetailsModal({
                                                                                                        type: 'optional',
                                                                                                        title: option.title,
                                                                                                        price: option.price,
                                                                                                        description: option.description || '',
                                                                                                        features: option.features || [],
                                                                                                        validity: option.validity
                                                                                                    })}
                                                                                                    style={{
                                                                                                        padding: '4px 10px',
                                                                                                        borderRadius: 16,
                                                                                                        background: option.selected ? '#f5f5f5' : '#fafbfc',
                                                                                                        color: '#222',
                                                                                                        cursor: 'pointer',
                                                                                                        border: option.selected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                                                                                        fontWeight: 500,
                                                                                                        fontSize: 13,
                                                                                                        transition: 'all 0.2s',
                                                                                                        display: 'inline-block',
                                                                                                        minWidth: 0
                                                                                                    }}
                                                                                                >
                                                                                                    {option.title} {priceText && <span style={{ fontWeight: 400, fontSize: 12 }}>{priceText}</span>}
                                                                                                </span>
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </>
                                                                );
                                                            })()}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        )}
                                        {/* Total & Select Row */}
                                        <tr>
                                            <td style={{ fontWeight: 700, fontSize: 16, color: '#222', background: '#fff', border: '1px solid #e0e0e0', padding: '20px 20px', textAlign: 'center', verticalAlign: 'middle' }}>Total</td>
                                            {getVisiblePlansWithPlaceholders().map((plan, visibleIdx) => {
                                                if (!plan) {
                                                    return (
                                                        <td key={`empty-total-${visibleIdx}`} style={{ padding: 20, background: '#fff', textAlign: 'center', border: '1px solid #e0e0e0' }}>
                                                        </td>
                                                    );
                                                }
                                                const planIdx = plansState.length === 1 ? 0 : plansStartIndex + visibleIdx;
                                                const recurring = getPlanRecurring(plan);
                                                const totalPrice = getPlanTotal(plan);
                                                const planPricingType = plan.pricing_type || 'fixed';
                                                const hasNullPrice = totalPrice === null;
                                                const isContactPricing = planPricingType === 'contact';
                                                const isExternalPricing = planPricingType === 'external';
                                                const hasExternalUrl = plan.external_url && plan.external_url.trim() !== '';

                                                return (
                                                    <td key={plan.id + '-total'} style={{ padding: 20, background: '#fff', textAlign: 'center', border: '1px solid #e0e0e0' }}>
                                                        <div style={{ fontWeight: 700, fontSize: 18, color: '#1976d2', marginBottom: 8 }}>
                                                            {(() => {
                                                                if (isContactPricing) {
                                                                    return (
                                                                        <a
                                                                            href="mailto:sales@bizserveus.com?subject=Pricing Inquiry"
                                                                            style={{ fontSize: 16, fontWeight: 500, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                                                                        >
                                                                            Contact for pricing
                                                                        </a>
                                                                    );
                                                                } else if (isExternalPricing) {
                                                                    // For external pricing, only show price if it exists, otherwise show nothing
                                                                    return (plan.price !== null && plan.price !== undefined) ? `$${formatPrice(plan.price)}` : null;
                                                                } else if (hasNullPrice) {
                                                                    // For fixed pricing with null price, show contact
                                                                    return (
                                                                        <a
                                                                            href="mailto:sales@bizserveus.com?subject=Pricing Inquiry"
                                                                            style={{ fontSize: 16, fontWeight: 500, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                                                                        >
                                                                            Contact for pricing
                                                                        </a>
                                                                    );
                                                                } else {
                                                                    // Show price if it exists
                                                                    return `$${totalPrice}`;
                                                                }
                                                            })()}
                                                        </div>
                                                        {Object.keys(recurring).length > 0 && (
                                                            <div style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>
                                                                {Object.entries(recurring).map(([period, amount]) => (
                                                                    <div key={period}>
                                                                        Per {period} from next {period}: <span style={{ fontWeight: 600, color: '#1976d2' }}>${amount.toFixed(2)}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {(() => {
                                                            const isActivePlan = isCurrentPlan(plan);
                                                            const isDisabled = !!(activePlan && !isActivePlan);
                                                            const baseBtnStyle = {
                                                                background: '#1976d2',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: 8,
                                                                padding: '8px 20px',
                                                                fontWeight: 700,
                                                                fontSize: 14,
                                                                cursor: isDisabled ? 'not-allowed' as const : 'pointer' as const,
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                                                opacity: isDisabled ? 0.5 : 1,
                                                                textDecoration: 'none' as const,
                                                            };
                                                            if (isActivePlan) {
                                                                return (
                                                                    <div
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: 6,
                                                                            background: '#2e7d32',
                                                                            color: '#fff',
                                                                            border: 'none',
                                                                            borderRadius: 8,
                                                                            padding: '8px 20px',
                                                                            fontWeight: 700,
                                                                            fontSize: 14,
                                                                        }}
                                                                    >
                                                                        <Check size={16} /> Current Plan
                                                                    </div>
                                                                );
                                                            }
                                                            if (isContactPricing || hasNullPrice) {
                                                                const ContactUsEl = isDisabled ? 'span' : 'a';
                                                                return (
                                                                    <ContactUsEl
                                                                        {...(!isDisabled && { href: "mailto:sales@bizserveus.com?subject=Pricing Inquiry" })}
                                                                        style={{
                                                                            ...baseBtnStyle,
                                                                            display: 'inline-block',
                                                                        }}
                                                                    >
                                                                        Contact Us
                                                                    </ContactUsEl>
                                                                );
                                                            }
                                                            if (isExternalPricing && hasExternalUrl) {
                                                                return !isEmployeeOrPartner ? (
                                                                    <button
                                                                        disabled={isDisabled}
                                                                        style={{
                                                                            ...baseBtnStyle,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            gap: 6,
                                                                        }}
                                                                        onClick={() => !isDisabled && handleSelect(planIdx)}
                                                                    >
                                                                        <ExternalLink size={16} /> Buy Now
                                                                    </button>
                                                                ) : null;
                                                            }
                                                            return !isEmployeeOrPartner ? (
                                                                <button
                                                                    disabled={isDisabled}
                                                                    style={baseBtnStyle}
                                                                    onClick={() => !isDisabled && handleSelect(planIdx)}
                                                                >
                                                                    Select
                                                                </button>
                                                            ) : null;
                                                        })()}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </TabPane>

                {/* Products & Services Tab */}
                <TabPane tabId="2">
                    <div style={{ position: 'relative' }}>
                        {/* Cart Icon */}
                        {!isEmployeeOrPartner && (
                            <div style={{ position: 'fixed', top: 90, right: 20, zIndex: 999 }}>
                                <button
                                    onClick={() => setShowCart(!showCart)}
                                    style={{
                                        background: '#1976d2',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: 60,
                                        height: 60,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        position: 'relative'
                                    }}
                                >
                                    <ShoppingCart size={24} />
                                    {getCartItemCount() > 0 && (
                                        <span style={{
                                            position: 'absolute',
                                            top: -5,
                                            right: -5,
                                            background: '#d32f2f',
                                            color: '#fff',
                                            borderRadius: '50%',
                                            width: 24,
                                            height: 24,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 12,
                                            fontWeight: 700
                                        }}>
                                            {getCartItemCount()}
                                        </span>
                                    )}
                                </button>
                            </div>
                        )}

                        {/* Cart Sidebar */}
                        {showCart && (
                            <div style={{
                                position: 'fixed',
                                top: 70,
                                right: 0,
                                width: 400,
                                height: 'calc(100vh - 70px)',
                                background: '#fff',
                                boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
                                zIndex: 1050,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <div style={{ padding: 20, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h4 style={{ margin: 0, fontWeight: 700 }}>Your Cart ({getCartItemCount()})</h4>
                                    <button onClick={() => setShowCart(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer' }}>×</button>
                                </div>

                                <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                                    {cart.length === 0 ? (
                                        <div style={{ textAlign: 'center', color: '#888', marginTop: 40 }}>
                                            Your cart is empty
                                        </div>
                                    ) : (
                                        cart.map(item => (
                                            <div key={item.product.id} style={{ marginBottom: 16, padding: 12, border: '1px solid #e0e0e0', borderRadius: 8 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                                    <div style={{ fontWeight: 600, flex: 1 }}>{item.product.title}</div>
                                                    <button onClick={() => removeFromCart(item.product.id)} style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer' }}>
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                                <div style={{ fontSize: 14, color: '#555', marginBottom: 8 }}>
                                                    {(() => {
                                                        const pricingType = item.product.pricing_type || 'fixed';
                                                        if (pricingType === 'contact') {
                                                            return 'Contact for pricing';
                                                        } else if (pricingType === 'external') {
                                                            return item.product.price !== null && item.product.price !== undefined ? `$${formatPrice(item.product.price)}` : '';
                                                        } else {
                                                            return item.product.price !== null && item.product.price !== undefined ? `$${formatPrice(item.product.price)}` : 'Contact for pricing';
                                                        }
                                                    })()}
                                                </div>
                                                {item.product.quantifiable && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <button
                                                            onClick={() => updateCartQuantity(item.product.id, -1)}
                                                            style={{
                                                                background: '#f5f5f5',
                                                                border: '1px solid #e0e0e0',
                                                                borderRadius: 4,
                                                                width: 28,
                                                                height: 28,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <Minus size={14} />
                                                        </button>
                                                        <span style={{ fontWeight: 600, minWidth: 30, textAlign: 'center' }}>{item.quantity}</span>
                                                        <button
                                                            onClick={() => updateCartQuantity(item.product.id, 1)}
                                                            style={{
                                                                background: '#f5f5f5',
                                                                border: '1px solid #e0e0e0',
                                                                borderRadius: 4,
                                                                width: 28,
                                                                height: 28,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <Plus size={14} />
                                                        </button>
                                                        <span style={{ marginLeft: 'auto', fontWeight: 600 }}>
                                                            {(() => {
                                                                const pricingType = item.product.pricing_type || 'fixed';
                                                                if (pricingType === 'contact' || (pricingType !== 'external' && (item.product.price === null || item.product.price === undefined))) {
                                                                    return 'N/A';
                                                                }
                                                                return item.product.price !== null && item.product.price !== undefined ? `$${formatPrice((parseFloat(String(item.product.price)) * item.quantity).toFixed(2))}` : 'N/A';
                                                            })()}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>

                                {cart.length > 0 && !isEmployeeOrPartner && (
                                    <div style={{ padding: 20, borderTop: '1px solid #e0e0e0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, fontSize: 18, fontWeight: 700 }}>
                                            <span>Total:</span>
                                            <span style={{ color: '#1976d2' }}>${getCartTotal()}</span>
                                        </div>
                                        <button
                                            onClick={handleCheckout}
                                            style={{
                                                width: '100%',
                                                background: '#1976d2',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: 8,
                                                padding: '12px 20px',
                                                fontWeight: 700,
                                                fontSize: 16,
                                                cursor: 'pointer',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.10)'
                                            }}
                                        >
                                            Proceed to Checkout
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Products Grid */}
                        <div style={{ padding: '20px 0' }}>
                            {!showloader && partners.length === 0 && (
                                <div className="text-center text-muted" style={{ padding: '24px 12px' }}>
                                    Products not available at this moment.
                                </div>
                            )}

                            {!showloader && partners.length > 0 && oneTimeProducts.length === 0 && (
                                <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>
                                    No products available at the moment.
                                </div>
                            )}

                            {!showloader && partners.length > 0 && oneTimeProducts.length > 0 && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
                                    {oneTimeProducts.map(product => (
                                        <div key={product.id} style={{
                                            background: '#fff',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 12,
                                            padding: 20,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.3s',
                                        }}
                                            onMouseEnter={e => {
                                                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                                                e.currentTarget.style.transform = 'translateY(-4px)';
                                            }}
                                            onMouseLeave={e => {
                                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            <h4 style={{ fontWeight: 700, fontSize: 18, marginBottom: 8, color: '#222' }}>{product.title}</h4>
                                            <div style={{ fontWeight: 600, fontSize: 20, color: '#1976d2', marginBottom: 12 }}>
                                                {(() => {
                                                    const pricingType = product.pricing_type || 'fixed';
                                                    if (pricingType === 'contact') {
                                                        return 'Contact for pricing';
                                                    } else if (pricingType === 'external') {
                                                        // For external, only show price if it exists
                                                        return product.price !== null && product.price !== undefined ? `$${formatPrice(product.price)}` : '';
                                                    } else {
                                                        // Fixed pricing
                                                        return product.price !== null && product.price !== undefined ? `$${formatPrice(product.price)}` : 'Contact for pricing';
                                                    }
                                                })()}
                                            </div>
                                            <div style={{ fontSize: 14, color: '#555', marginBottom: 12, flex: 1 }}>
                                                <ReactMarkdown
                                                    components={{
                                                        p: ({ children }) => <p style={{ marginBottom: '8px', marginTop: 0, fontSize: 14, color: '#555' }}>{children}</p>,
                                                        strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                                        em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                        ul: ({ children }) => <ul style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 14, color: '#555' }}>{children}</ul>,
                                                        ol: ({ children }) => <ol style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 14, color: '#555' }}>{children}</ol>,
                                                        li: ({ children }) => <li style={{ marginBottom: '4px', fontSize: 14, color: '#555' }}>{children}</li>,
                                                        a: ({ children, href }) => <a href={href} style={{ color: '#1976d2', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                                    }}
                                                >
                                                    {cleanMarkdown(product.description)}
                                                </ReactMarkdown>
                                            </div>
                                            {product.features && product.features.length > 0 && (
                                                <ul style={{ listStyle: 'disc', fontSize: 13, color: '#666', paddingLeft: 18, margin: 0, marginBottom: 16 }}>
                                                    {product.features.map((feature, idx) => (
                                                        <li key={idx}>{feature}</li>
                                                    ))}
                                                </ul>
                                            )}
                                            {(() => {
                                                const pricingType = product.pricing_type || 'fixed';

                                                // Show cart controls only if pricing is not 'contact'
                                                if (pricingType === 'contact') {
                                                    return !isEmployeeOrPartner ? (
                                                        <a
                                                            href="mailto:sales@bizserveus.com?subject=Pricing Inquiry"
                                                            style={{
                                                                background: '#1976d2',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: 8,
                                                                padding: '10px 20px',
                                                                fontWeight: 600,
                                                                fontSize: 14,
                                                                cursor: 'pointer',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                                                textDecoration: 'none',
                                                                display: 'block',
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            Contact Us
                                                        </a>
                                                    ) : null;
                                                }

                                                // For external or fixed pricing, show cart controls
                                                const cartItem = cart.find(item => item.product.id === product.id);
                                                const inCart = !!cartItem;
                                                const quantity = cartItem?.quantity || 0;

                                                if (inCart) {
                                                    // Hide cart controls for employees and partners
                                                    if (isEmployeeOrPartner) {
                                                        return null;
                                                    }
                                                    if (product.quantifiable) {
                                                        // Show quantity controls
                                                        return (
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                background: '#f5f5f5',
                                                                borderRadius: 8,
                                                                padding: '8px 12px',
                                                                border: '2px solid #1976d2'
                                                            }}>
                                                                <button
                                                                    onClick={() => updateCartQuantity(product.id, -1)}
                                                                    style={{
                                                                        background: '#1976d2',
                                                                        color: '#fff',
                                                                        border: 'none',
                                                                        borderRadius: 6,
                                                                        width: 32,
                                                                        height: 32,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        cursor: 'pointer',
                                                                        fontWeight: 700
                                                                    }}
                                                                >
                                                                    <Minus size={16} />
                                                                </button>
                                                                <span style={{ fontWeight: 700, fontSize: 16, color: '#1976d2', minWidth: 40, textAlign: 'center' }}>
                                                                    {quantity}
                                                                </span>
                                                                <button
                                                                    onClick={() => updateCartQuantity(product.id, 1)}
                                                                    style={{
                                                                        background: '#1976d2',
                                                                        color: '#fff',
                                                                        border: 'none',
                                                                        borderRadius: 6,
                                                                        width: 32,
                                                                        height: 32,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        cursor: 'pointer',
                                                                        fontWeight: 700
                                                                    }}
                                                                >
                                                                    <Plus size={16} />
                                                                </button>
                                                            </div>
                                                        );
                                                    } else {
                                                        // Show "Added to Cart"
                                                        return (
                                                            <button
                                                                disabled
                                                                style={{
                                                                    background: '#4caf50',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    borderRadius: 8,
                                                                    padding: '10px 20px',
                                                                    fontWeight: 600,
                                                                    fontSize: 14,
                                                                    cursor: 'default',
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: 8
                                                                }}
                                                            >
                                                                <ShoppingCart size={16} /> Added to Cart
                                                            </button>
                                                        );
                                                    }
                                                } else {
                                                    // Show "Add to Cart" or "Buy Now" (only if not employee or partner)
                                                    if (isEmployeeOrPartner) {
                                                        return null;
                                                    }

                                                    // Check if product has external pricing
                                                    const isExternal = pricingType === 'external' && product.external_url;

                                                    return (
                                                        <button
                                                            onClick={() => addToCart(product)}
                                                            style={{
                                                                background: '#1976d2',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: 8,
                                                                padding: '10px 20px',
                                                                fontWeight: 600,
                                                                fontSize: 14,
                                                                cursor: 'pointer',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: 8
                                                            }}
                                                        >
                                                            {isExternal ? (
                                                                <>
                                                                    <ExternalLink size={16} /> Buy Now
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <ShoppingCart size={16} /> Add to Cart
                                                                </>
                                                            )}
                                                        </button>
                                                    );
                                                }
                                            })()}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </TabPane>

                {/* Sales support Tab */}
                <TabPane tabId="3">
                    <PartnerCommunicationPreferences
                        partnerId={selectedPartnerId}
                        partnerName={getSelectedPartnerName()}
                        loadingCommPrefs={loadingCommPrefs}
                        commPreferences={commPreferences}
                        noPartnerMessage="Please select a partner to view communication preferences."
                        mode="before"
                    />
                </TabPane>
            </TabContent>

            {/* Partner Details Modal */}
            <Modal isOpen={partnerDetailsModal} toggle={() => setPartnerDetailsModal(false)} size="lg" className="partner-details-modal">
                <ModalHeader toggle={() => setPartnerDetailsModal(false)}>
                    Partner Details
                </ModalHeader>
                <ModalBody style={{ maxHeight: 'calc(90vh - 100px)', overflowY: 'auto' }}>
                    {selectedPartnerDetails && (
                        <div>
                            {/* Company/Individual Name and Address at Top */}
                            <div className="mb-4">
                                <Row>
                                    <Col md={12}>
                                        <div className="d-flex align-items-center gap-3 mb-3">
                                            {!selectedPartnerDetails.company_name && selectedPartnerDetails.user?.profile_pic && (
                                                <img
                                                    src={`${process.env.REACT_APP_BASE_URL}${selectedPartnerDetails.user.profile_pic}`}
                                                    alt="Partner"
                                                    style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        borderRadius: '50%',
                                                        objectFit: 'cover',
                                                        border: '2px solid #dee2e6'
                                                    }}
                                                />
                                            )}
                                            {!selectedPartnerDetails.company_name && !selectedPartnerDetails.user?.profile_pic && (
                                                <div
                                                    style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        borderRadius: '50%',
                                                        background: '#e9ecef',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '32px',
                                                        color: '#6c757d'
                                                    }}
                                                >
                                                    {selectedPartnerDetails.user?.first_name?.[0] || 'P'}
                                                </div>
                                            )}
                                            <div className="flex-grow-1">
                                                <h4 className="mb-2 d-flex align-items-center gap-2">
                                                    {selectedPartnerDetails.company_name ? (
                                                        <>
                                                            <Building2 size={24} />
                                                            {selectedPartnerDetails.company_name}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Building2 size={24} />
                                                            {selectedPartnerDetails.user?.first_name} {selectedPartnerDetails.user?.last_name}
                                                        </>
                                                    )}
                                                </h4>
                                                {!selectedPartnerDetails.company_name && (
                                                    <>
                                                        {selectedPartnerDetails.user?.email && (
                                                            <p className="text-muted mb-1">
                                                                {selectedPartnerDetails.user.email}
                                                            </p>
                                                        )}
                                                        {selectedPartnerDetails.user?.phone && (
                                                            <p className="text-muted mb-2">
                                                                <Phone size={14} style={{ display: 'inline', marginRight: '4px' }} />
                                                                {selectedPartnerDetails.user.phone}
                                                            </p>
                                                        )}
                                                    </>
                                                )}
                                                {(selectedPartnerDetails.address_line1 || selectedPartnerDetails.city || selectedPartnerDetails.state) && (
                                                    <div className="text-muted">
                                                        {selectedPartnerDetails.address_line1 && <div>{selectedPartnerDetails.address_line1}</div>}
                                                        {selectedPartnerDetails.address_line2 && <div>{selectedPartnerDetails.address_line2}</div>}
                                                        {(selectedPartnerDetails.city || selectedPartnerDetails.state || selectedPartnerDetails.zipcode) && (
                                                            <div>
                                                                {selectedPartnerDetails.city}
                                                                {selectedPartnerDetails.city && selectedPartnerDetails.state && ', '}
                                                                {selectedPartnerDetails.state}
                                                                {selectedPartnerDetails.zipcode && ` ${selectedPartnerDetails.zipcode}`}
                                                            </div>
                                                        )}
                                                        {selectedPartnerDetails.country && <div>{selectedPartnerDetails.country}</div>}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* Point of Contact (only if company exists) */}
                            {selectedPartnerDetails.company_name && (
                                <div className="mb-4">
                                    <h5 className="mb-3 d-flex align-items-center gap-2">
                                        <Phone size={20} />
                                        Point of Contact
                                    </h5>
                                    <Row>
                                        <Col md={12}>
                                            <div className="d-flex align-items-center gap-3 mb-3">
                                                {selectedPartnerDetails.user?.profile_pic ? (
                                                    <img
                                                        src={`${process.env.REACT_APP_BASE_URL}${selectedPartnerDetails.user.profile_pic}`}
                                                        alt="Partner"
                                                        style={{
                                                            width: '80px',
                                                            height: '80px',
                                                            borderRadius: '50%',
                                                            objectFit: 'cover',
                                                            border: '2px solid #dee2e6'
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        style={{
                                                            width: '80px',
                                                            height: '80px',
                                                            borderRadius: '50%',
                                                            background: '#e9ecef',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '32px',
                                                            color: '#6c757d'
                                                        }}
                                                    >
                                                        {selectedPartnerDetails.user?.first_name?.[0] || 'P'}
                                                    </div>
                                                )}
                                                <div>
                                                    <h5 className="mb-1">
                                                        {selectedPartnerDetails.user?.first_name} {selectedPartnerDetails.user?.last_name}
                                                    </h5>
                                                    <p className="text-muted mb-0">
                                                        {selectedPartnerDetails.user?.email}
                                                    </p>
                                                    {selectedPartnerDetails.user?.phone && (
                                                        <p className="text-muted mb-0">
                                                            <Phone size={14} style={{ display: 'inline', marginRight: '4px' }} />
                                                            {selectedPartnerDetails.user.phone}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Company Information */}
                            {(selectedPartnerDetails.company_description || selectedPartnerDetails.industry || selectedPartnerDetails.company_size ||
                                selectedPartnerDetails.years_in_business || selectedPartnerDetails.number_of_employees ||
                                selectedPartnerDetails.service_areas || selectedPartnerDetails.specializations) && (
                                    <div className="mb-4">
                                        <h5 className="mb-3 d-flex align-items-center gap-2">
                                            <Building2 size={20} />
                                            Company Information
                                        </h5>
                                        <Row>
                                            {selectedPartnerDetails.company_description && (
                                                <Col md={12} className="mb-2">
                                                    <strong>About:</strong>
                                                    <p className="text-muted mt-1">{selectedPartnerDetails.company_description}</p>
                                                </Col>
                                            )}
                                            {selectedPartnerDetails.industry && (
                                                <Col md={6} className="mb-2">
                                                    <strong>Industry:</strong> {selectedPartnerDetails.industry}
                                                </Col>
                                            )}
                                            {selectedPartnerDetails.company_size && (
                                                <Col md={6} className="mb-2">
                                                    <strong>Company Size:</strong> {selectedPartnerDetails.company_size}
                                                </Col>
                                            )}
                                            {selectedPartnerDetails.years_in_business && (
                                                <Col md={6} className="mb-2">
                                                    <strong>Years in Business:</strong> {selectedPartnerDetails.years_in_business}
                                                </Col>
                                            )}
                                            {selectedPartnerDetails.number_of_employees && (
                                                <Col md={6} className="mb-2">
                                                    <strong>Employees:</strong> {selectedPartnerDetails.number_of_employees}
                                                </Col>
                                            )}
                                            {selectedPartnerDetails.service_areas && (
                                                <Col md={12} className="mb-2">
                                                    <strong>Service Areas:</strong>
                                                    <p className="text-muted mt-1">{selectedPartnerDetails.service_areas}</p>
                                                </Col>
                                            )}
                                            {selectedPartnerDetails.specializations && (
                                                <Col md={12} className="mb-2">
                                                    <strong>Specializations:</strong>
                                                    <p className="text-muted mt-1">{selectedPartnerDetails.specializations}</p>
                                                </Col>
                                            )}
                                        </Row>
                                    </div>
                                )}

                            {/* Website & Social Media */}
                            {(selectedPartnerDetails.website || selectedPartnerDetails.linkedin_url || selectedPartnerDetails.facebook_url ||
                                selectedPartnerDetails.x_url || selectedPartnerDetails.instagram_url || selectedPartnerDetails.youtube_url) && (
                                    <div className="mb-4">
                                        <h5 className="mb-3 d-flex align-items-center gap-2">
                                            <Globe size={20} />
                                            Website & Social Media
                                        </h5>
                                        <Row>
                                            {selectedPartnerDetails.website && (
                                                <Col md={6} className="mb-2">
                                                    <a href={selectedPartnerDetails.website} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2">
                                                        <Globe size={16} />
                                                        <span>Website</span>
                                                        <ExternalLink size={14} />
                                                    </a>
                                                </Col>
                                            )}
                                            {selectedPartnerDetails.linkedin_url && (
                                                <Col md={6} className="mb-2">
                                                    <a href={selectedPartnerDetails.linkedin_url} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2">
                                                        <Linkedin size={16} />
                                                        <span>LinkedIn</span>
                                                        <ExternalLink size={14} />
                                                    </a>
                                                </Col>
                                            )}
                                            {selectedPartnerDetails.facebook_url && (
                                                <Col md={6} className="mb-2">
                                                    <a href={selectedPartnerDetails.facebook_url} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2">
                                                        <Facebook size={16} />
                                                        <span>Facebook</span>
                                                        <ExternalLink size={14} />
                                                    </a>
                                                </Col>
                                            )}
                                            {selectedPartnerDetails.x_url && (
                                                <Col md={6} className="mb-2">
                                                    <a href={selectedPartnerDetails.x_url} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2">
                                                        <Twitter size={16} />
                                                        <span>X (Twitter)</span>
                                                        <ExternalLink size={14} />
                                                    </a>
                                                </Col>
                                            )}
                                            {selectedPartnerDetails.instagram_url && (
                                                <Col md={6} className="mb-2">
                                                    <a href={selectedPartnerDetails.instagram_url} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2">
                                                        <Instagram size={16} />
                                                        <span>Instagram</span>
                                                        <ExternalLink size={14} />
                                                    </a>
                                                </Col>
                                            )}
                                            {selectedPartnerDetails.youtube_url && (
                                                <Col md={6} className="mb-2">
                                                    <a href={selectedPartnerDetails.youtube_url} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2">
                                                        <Youtube size={16} />
                                                        <span>YouTube</span>
                                                        <ExternalLink size={14} />
                                                    </a>
                                                </Col>
                                            )}
                                        </Row>
                                    </div>
                                )}

                            {/* Tools */}
                            {(selectedPartnerDetails.bookkeeping_tools?.length > 0 || selectedPartnerDetails.tax_tools?.length > 0) && (
                                <div className="mb-4">
                                    <h5 className="mb-3">Tools & Expertise</h5>
                                    {selectedPartnerDetails.bookkeeping_tools?.length > 0 && (
                                        <div className="mb-2">
                                            <strong>Bookkeeping Tools:</strong>
                                            <div className="mt-1">
                                                {selectedPartnerDetails.bookkeeping_tools.map((tool: string, idx: number) => (
                                                    <Badge key={idx} color="info" className="me-1 mb-1">{tool}</Badge>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {selectedPartnerDetails.tax_tools?.length > 0 && (
                                        <div>
                                            <strong>Tax Tools:</strong>
                                            <div className="mt-1">
                                                {selectedPartnerDetails.tax_tools.map((tool: string, idx: number) => (
                                                    <Badge key={idx} color="success" className="me-1 mb-1">{tool}</Badge>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Domains */}
                            {selectedPartnerDetails.domains_names?.length > 0 && (
                                <div className="mb-4">
                                    <h5 className="mb-3">Domains</h5>
                                    <div>
                                        {selectedPartnerDetails.domains_names.map((domain: string, idx: number) => (
                                            <Badge key={idx} color="primary" className="me-1 mb-1">{domain}</Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </ModalBody>
            </Modal>

            {/* Product Details Modal */}
            <Modal isOpen={productDetailsModal} toggle={() => setProductDetailsModal(false)} size="lg" className="product-details-modal">
                <style>{`
                    .product-details-modal .modal-header {
                        position: relative;
                        padding-top: 1rem;
                        padding-bottom: 0;
                        border-bottom: none;
                    }
                    .product-details-modal .modal-header .close {
                        position: absolute;
                        top: 0;
                        right: 0;
                        padding: 1rem;
                        margin: 0;
                        z-index: 1;
                    }
                `}</style>
                <ModalHeader toggle={() => setProductDetailsModal(false)}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#222', lineHeight: 1.2, marginBottom: 16 }}>
                        {selectedProductDetails?.title}
                    </div>
                </ModalHeader>
                <ModalBody style={{ maxHeight: 'calc(90vh - 100px)', overflowY: 'auto', paddingTop: 20 }}>
                    {/* Description - Above price */}
                    {selectedProductDetails && selectedProductDetails.description && (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{
                                fontSize: 15,
                                color: '#444',
                                lineHeight: 1.8,
                                letterSpacing: '0.01em'
                            }}>
                                <ReactMarkdown
                                    components={{
                                        p: ({ children }) => <p style={{ marginBottom: '12px', marginTop: 0, fontSize: 15, color: '#444', lineHeight: 1.8 }}>{children}</p>,
                                        strong: ({ children }) => <strong style={{ fontWeight: 600, color: '#222' }}>{children}</strong>,
                                        em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                        ul: ({ children }) => <ul style={{ marginBottom: '12px', paddingLeft: '24px', fontSize: 15, color: '#444' }}>{children}</ul>,
                                        ol: ({ children }) => <ol style={{ marginBottom: '12px', paddingLeft: '24px', fontSize: 15, color: '#444' }}>{children}</ol>,
                                        li: ({ children }) => <li style={{ marginBottom: '6px', fontSize: 15, color: '#444', lineHeight: 1.7 }}>{children}</li>,
                                        a: ({ children, href }) => <a href={href} style={{ color: '#1976d2', textDecoration: 'none', borderBottom: '1px solid #1976d2' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                    }}
                                >
                                    {cleanMarkdown(selectedProductDetails.description)}
                                </ReactMarkdown>
                            </div>
                        </div>
                    )}
                    {/* Price - Subtle */}
                    {selectedProductDetails && (
                        <div style={{ marginTop: 8, marginBottom: 0 }}>
                            {(() => {
                                // Note: selectedProductDetails doesn't have pricing_type, so we check price directly
                                // This is for addons/optionals which always use fixed pricing
                                if (selectedProductDetails.price !== null && selectedProductDetails.price !== undefined) {
                                    return (
                                        <div style={{ fontSize: 20, fontWeight: 600, color: '#1976d2' }}>
                                            ${formatPrice(selectedProductDetails.price)}
                                            {(selectedProductDetails.period || (selectedProductDetails.validity && selectedProductDetails.validity !== '' && selectedProductDetails.validity !== 'lifetime' && selectedProductDetails.validity !== 'custom')) && (
                                                <span style={{ fontSize: 16, color: '#666', fontWeight: 500, marginLeft: 6 }}>
                                                    / {selectedProductDetails.period || selectedProductDetails.validity}
                                                </span>
                                            )}
                                        </div>
                                    );
                                } else {
                                    return (
                                        <div style={{ fontSize: 18, fontWeight: 500, color: '#1976d2' }}>
                                            Contact for pricing
                                        </div>
                                    );
                                }
                            })()}
                        </div>
                    )}
                    {selectedProductDetails && (
                        <div>

                            {/* Features Section - Bullet List */}
                            {selectedProductDetails.features && selectedProductDetails.features.length > 0 && (
                                <div style={{
                                    borderTop: '2px solid #e8ecf1',
                                    paddingTop: 24
                                }}>
                                    <ul style={{ listStyle: 'disc', paddingLeft: 18, margin: 0, fontSize: 14, color: '#333', lineHeight: 1.6 }}>
                                        {selectedProductDetails.features.map((feature, idx) => (
                                            <li key={idx} style={{ marginBottom: 8 }}>{feature}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </ModalBody>
            </Modal>

            {/* Purchase Agreement Modal */}
            <Modal isOpen={purchaseAgreementModal} toggle={() => {
                setPurchaseAgreementModal(false);
                setPendingCheckoutData(null);
                setAcceptedAgreementIds([]);
                setDisclaimerAccepted(false);
            }} size="lg" className="purchase-agreement-modal">
                <ModalHeader toggle={() => {
                    setPurchaseAgreementModal(false);
                    setPendingCheckoutData(null);
                    setAcceptedAgreementIds([]);
                    setDisclaimerAccepted(false);
                }}>
                    {purchaseAgreements.length > 0 ? "Purchase Agreements" : "Before You Proceed"}
                </ModalHeader>
                <ModalBody style={{ maxHeight: 'calc(90vh - 100px)', overflowY: 'auto' }}>
                    {purchaseAgreements.length > 0 && (
                        <div className="mb-3">
                            <p className="text-muted">
                                Please review and accept the following purchase agreements before proceeding with checkout.
                            </p>
                        </div>
                    )}

                    {isLoadingAgreements ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : purchaseAgreements.length > 0 ? (
                        <div>
                            {purchaseAgreements.map((agreement: any) => {
                                const isRequired = !agreement.optional;
                                const isAccepted = acceptedAgreementIds.includes(agreement.id);

                                return (
                                    <div key={agreement.id} className="mb-3">
                                        <Label check className="d-flex align-items-center">
                                            <input
                                                type="checkbox"
                                                checked={isAccepted}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setAcceptedAgreementIds([...acceptedAgreementIds, agreement.id]);
                                                    } else {
                                                        setAcceptedAgreementIds(acceptedAgreementIds.filter(id => id !== agreement.id));
                                                    }
                                                }}
                                                className="form-check-input me-2"
                                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                            />
                                            <span style={{ cursor: 'pointer' }}>
                                                I accept{' '}
                                                {agreement.view_url ? (
                                                    <a
                                                        href={agreement.view_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="agreement-link"
                                                    >
                                                        {agreement.file_name?.replace(/\.[^/.]+$/, '') || agreement.file_name}
                                                    </a>
                                                ) : (
                                                    <span>{agreement.file_name?.replace(/\.[^/.]+$/, '') || agreement.file_name}</span>
                                                )}
                                                {isRequired && <span className="text-danger ms-1">*</span>}
                                            </span>
                                        </Label>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                    <p className="text-muted text-end">
                        {requiredAgreementIds.length > 0 && (
                            <span className="text-danger"> * Required agreements must be accepted.</span>
                        )}
                    </p>

                    {checkoutDisclaimer && (
                        <div className="mt-3 pt-3 border-top">
                            <Label check className="d-flex align-items-start gap-2" style={{ cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={disclaimerAccepted}
                                    onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                                    className="form-check-input mt-1"
                                    style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                                />
                                <span>
                                    {checkoutDisclaimer.text}
                                    <span className="text-danger ms-1">*</span>
                                </span>
                            </Label>
                        </div>
                    )}
                    <style>{`
                        #proceed-checkout-btn .btn-primary:disabled {
                            background-color: #6c757d !important;
                            border-color: #6c757d !important;
                            cursor: not-allowed !important;
                            opacity: 1 !important;
                        }
                        #proceed-checkout-btn .btn-primary:disabled:hover {
                            background-color: #6c757d !important;
                            border-color: #6c757d !important;
                        }
                        .agreement-link {
                            color: #0d6efd !important;
                            text-decoration: none !important;
                            font-weight: 400 !important;
                        }
                        .agreement-link:hover {
                            text-decoration: underline !important;
                        }
                    `}</style>
                    <div className="mt-4 d-flex justify-content-end gap-2">
                        <Button
                            color="secondary"
                            onClick={() => {
                                setPurchaseAgreementModal(false);
                                setPendingCheckoutData(null);
                                setAcceptedAgreementIds([]);
                                setDisclaimerAccepted(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <span id="proceed-checkout-btn">
                            <Button
                                color="primary"
                                onClick={proceedWithCheckout}
                                disabled={
                                    isLoadingAgreements ||
                                    (checkoutDisclaimer && !disclaimerAccepted) ||
                                    (requiredAgreementIds.length > 0 &&
                                    requiredAgreementIds.some(id => !acceptedAgreementIds.includes(id)))
                                }
                            >
                                {isLoadingAgreements ? "Processing..." : "Proceed to Checkout"}
                            </Button>
                        </span>
                        {(isLoadingAgreements ||
                            (requiredAgreementIds.length > 0 &&
                            requiredAgreementIds.some(id => !acceptedAgreementIds.includes(id)))) && (
                            <UncontrolledTooltip placement="top" target="proceed-checkout-btn">
                                Accept all required agreements to proceed further
                            </UncontrolledTooltip>
                        )}
                    </div>
                </ModalBody>
            </Modal>
        </div>
    );
};

export default PackagesPricing;
